import { Link } from "react-router-dom";
import type { CompanySearchResult } from "@sales-ops-amplify/shared";
import { workspaceAccountDetailId } from "@/lib/workspace-account";
import { cn } from "@/cn";

function locationOf(c: CompanySearchResult): string {
  return (
    c.fullAddress ||
    [c.city, c.state, c.country].filter(Boolean).join(", ") ||
    "—"
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

/** Stable row id when apolloOrgId is absent (directory-only companies). */
export function companyRowId(c: CompanySearchResult): string {
  return c.apolloOrgId ?? c.companyId;
}

export function CompanyResultsTable({
  companies,
  selectedIds,
  onToggle,
  onToggleAll,
  onOpen,
}: {
  companies: CompanySearchResult[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: (all: boolean) => void;
  onOpen: (company: CompanySearchResult) => void;
}) {
  const allSelected =
    companies.length > 0 &&
    companies.every((c) => selectedIds.has(companyRowId(c)));

  if (companies.length === 0) {
    return (
      <div className="border-t border-border px-4 py-16 text-center text-sm text-muted-foreground">
        No companies yet. Adjust filters and search.
      </div>
    );
  }

  return (
    <div className="relative min-h-0 min-w-0 flex-1 overflow-auto overscroll-x-contain [-webkit-overflow-scrolling:touch]">
      <table className="w-full min-w-[1200px] border-separate border-spacing-0 text-sm">
        <thead className="sticky top-0 z-10">
          <tr className="bg-card text-left text-[10px] uppercase tracking-wide text-muted-foreground shadow-[inset_0_-1px_0_var(--border)]">
            <th className="w-9 bg-card px-3 py-2.5">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={(e) => onToggleAll(e.target.checked)}
              />
            </th>
            <th className="bg-card px-3 py-2.5 font-semibold">Company</th>
            <th className="bg-card px-3 py-2.5 font-semibold">Domain</th>
            <th className="bg-card px-3 py-2.5 font-semibold">Industry</th>
            <th className="bg-card px-3 py-2.5 font-semibold"># Emp</th>
            <th className="bg-card px-3 py-2.5 font-semibold">Location</th>
            <th className="bg-card px-3 py-2.5 font-semibold">Stage</th>
            <th className="bg-card px-3 py-2.5 font-semibold">Owner</th>
            <th className="bg-card px-3 py-2.5 font-semibold">Funding</th>
            <th className="bg-card px-3 py-2.5 font-semibold">Links</th>
          </tr>
        </thead>
        <tbody>
          {companies.map((c) => {
            const rowId = companyRowId(c);
            const selected = selectedIds.has(rowId);
            const accountId = workspaceAccountDetailId(c);
            const directoryId =
              c.companyId ?? c.accountId ?? c.apolloOrgId ?? rowId;
            const detailPath = c.alreadySaved && accountId
              ? `/user/accounts/${accountId}`
              : `/companies/${directoryId}`;
            return (
              <tr
                key={rowId}
                className={cn(
                  "border-b border-border hover:bg-muted/10",
                  selected && "bg-brand-soft/40",
                  c.alreadySaved && !selected && "bg-muted/5",
                )}
              >
                <td className="border-b border-border px-3 py-2.5">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => onToggle(rowId)}
                  />
                </td>
                <td className="border-b border-border px-3 py-2.5">
                  <div className="flex min-w-0 items-center gap-2">
                    {c.logoUrl ? (
                      <img
                        src={c.logoUrl}
                        alt=""
                        className="size-8 rounded-md object-contain"
                      />
                    ) : (
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-brand/15 text-[10px] font-semibold text-brand">
                        {initials(c.name)}
                      </div>
                    )}
                    <div className="min-w-0">
                      <button
                        type="button"
                        onClick={() => onOpen(c)}
                        className="block truncate text-left text-[13px] font-semibold text-brand hover:underline"
                      >
                        {c.name}
                      </button>
                      <p className="truncate text-[11px] text-muted-foreground">
                        {c.alreadySaved ? "In accounts" : c.foundedYear ?? "—"}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="border-b border-border px-3 py-2.5 text-[12px]">
                  {c.domain ?? "—"}
                </td>
                <td className="max-w-[140px] truncate border-b border-border px-3 py-2.5 text-[12px] text-muted-foreground">
                  {c.industry ?? "—"}
                </td>
                <td className="border-b border-border px-3 py-2.5 text-[12px] text-muted-foreground">
                  {c.employeeCount?.toLocaleString() ?? "—"}
                </td>
                <td className="max-w-[160px] truncate border-b border-border px-3 py-2.5 text-[12px] text-muted-foreground">
                  {locationOf(c)}
                </td>
                <td className="border-b border-border px-3 py-2.5 text-[12px] text-muted-foreground">
                  {c.accountStage ?? "—"}
                </td>
                <td className="max-w-[140px] truncate border-b border-border px-3 py-2.5 text-[12px] text-muted-foreground">
                  {c.accountOwner || "—"}
                </td>
                <td className="border-b border-border px-3 py-2.5 text-[12px] text-muted-foreground">
                  {c.latestFunding ??
                    (c.totalFunding != null
                      ? String(c.totalFunding)
                      : "—")}
                </td>
                <td className="border-b border-border px-3 py-2.5">
                  <div className="flex items-center gap-2 text-[11px]">
                    <Link
                      to={detailPath}
                      className="text-brand hover:underline"
                    >
                      Open
                    </Link>
                    {c.linkedinUrl && (
                      <a
                        href={c.linkedinUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-brand hover:underline"
                      >
                        LinkedIn
                      </a>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
